/**
 * Hand-rolled SVG / CSS charts — NO charting library (bundle-free, themeable).
 *
 * Every chart draws with `currentColor`, so the caller sets the hue with a
 * Tailwind `text-*` class and it adapts to light / dark automatically. SVGs use
 * a viewBox + `preserveAspectRatio="none"` to stay responsive, and
 * `vectorEffect="non-scaling-stroke"` so line weight stays crisp when scaled.
 */

import type { ReactNode } from 'react';

// --- Number formatting ------------------------------------------------------

export function compactNumber(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
  return `${Math.round(value)}`;
}

export function formatPct(value: number): string {
  return `${value.toFixed(value >= 10 || value <= -10 ? 0 : 1)}%`;
}

// --- KPI card ---------------------------------------------------------------

export function KpiCard({
  label,
  value,
  sub,
  accent = 'text-primary',
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  accent?: string;
}) {
  return (
    <div className="rounded-token-lg border border-border bg-surface px-4 py-3 shadow-elevation-1">
      <div className="text-xs font-medium uppercase tracking-wide text-muted">{label}</div>
      <div className={`mt-1 text-2xl font-bold tabular-nums ${accent}`}>{value}</div>
      {sub != null && <div className="mt-0.5 text-xs text-muted">{sub}</div>}
    </div>
  );
}

// --- Sparkline --------------------------------------------------------------

export function Sparkline({
  values,
  width = 120,
  height = 32,
  className = 'text-primary',
}: {
  values: number[];
  width?: number;
  height?: number;
  className?: string;
}) {
  if (values.length < 2) {
    return <div className="text-xs text-muted">—</div>;
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const stepX = width / (values.length - 1);
  const points = values
    .map((v, i) => `${(i * stepX).toFixed(2)},${(height - ((v - min) / span) * height).toFixed(2)}`)
    .join(' ');
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={className}
      role="img"
      aria-hidden
    >
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

// --- Line chart (single series, area fill, min/max guides) ------------------

export interface LinePoint {
  label: string;
  value: number;
}

export function LineChart({
  points,
  height = 180,
  valueFormat = compactNumber,
  className = 'text-primary',
}: {
  points: LinePoint[];
  height?: number;
  valueFormat?: (n: number) => string;
  className?: string;
}) {
  if (points.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-muted">
        Aucune donnée
      </div>
    );
  }
  const W = 640;
  const H = height;
  const padY = 12;
  const values = points.map((p) => p.value);
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 1);
  const span = max - min || 1;
  const stepX = points.length > 1 ? W / (points.length - 1) : 0;
  const y = (v: number) => padY + (1 - (v - min) / span) * (H - padY * 2);
  const coords = points.map((p, i) => ({ x: i * stepX, y: y(p.value) }));
  const line = coords.map((c) => `${c.x.toFixed(2)},${c.y.toFixed(2)}`).join(' ');
  const area = `${line} ${(W).toFixed(2)},${H} 0,${H}`;
  const firstLabel = points[0].label;
  const lastLabel = points[points.length - 1].label;

  return (
    <div className={className}>
      <div className="flex justify-between text-xs text-muted">
        <span>{valueFormat(max)}</span>
        <span>{valueFormat(min)}</span>
      </div>
      <svg
        width="100%"
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Courbe temporelle"
      >
        <polygon points={area} fill="currentColor" fillOpacity={0.1} />
        <polyline
          points={line}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
        {coords.map((c, i) => (
          <circle key={i} cx={c.x} cy={c.y} r={2.5} fill="currentColor" vectorEffect="non-scaling-stroke" />
        ))}
      </svg>
      <div className="mt-1 flex justify-between text-xs text-muted">
        <span>{firstLabel}</span>
        {points.length > 2 && <span>{points[Math.floor(points.length / 2)].label}</span>}
        <span>{lastLabel}</span>
      </div>
    </div>
  );
}

// --- Bar chart (vertical, palette per bar) ----------------------------------

export interface BarDatum {
  label: string;
  value: number;
}

const BAR_COLORS = [
  'fill-indigo-500',
  'fill-emerald-500',
  'fill-sky-500',
  'fill-amber-500',
  'fill-rose-500',
  'fill-violet-500',
  'fill-teal-500',
  'fill-orange-500',
];

export function BarChart({
  data,
  height = 180,
  valueFormat = compactNumber,
}: {
  data: BarDatum[];
  height?: number;
  valueFormat?: (n: number) => string;
}) {
  if (data.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-muted">
        Aucune donnée
      </div>
    );
  }
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="flex items-end gap-2 overflow-x-auto" style={{ height: height + 44 }}>
      {data.map((d, i) => {
        const h = Math.max(2, Math.round((d.value / max) * height));
        return (
          <div key={`${d.label}-${i}`} className="flex min-w-[40px] flex-1 flex-col items-center">
            <div className="mb-1 text-xs font-medium tabular-nums text-foreground">
              {valueFormat(d.value)}
            </div>
            <svg width="100%" height={height} viewBox={`0 0 10 ${height}`} preserveAspectRatio="none">
              <rect
                x={1}
                y={height - h}
                width={8}
                height={h}
                rx={1}
                className={BAR_COLORS[i % BAR_COLORS.length]}
              />
            </svg>
            <div className="mt-1 w-full truncate text-center text-xs text-muted" title={d.label}>
              {d.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// --- Horizontal proportion bars (labelled rows) -----------------------------

export function ProportionBars({
  data,
  valueFormat = compactNumber,
}: {
  data: BarDatum[];
  valueFormat?: (n: number) => string;
}) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div className="space-y-3">
      {data.map((d, i) => (
        <div key={`${d.label}-${i}`}>
          <div className="mb-1 flex items-baseline justify-between text-xs">
            <span className="font-medium capitalize text-foreground">{d.label}</span>
            <span className="tabular-nums text-muted">{valueFormat(d.value)}</span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-surface-2">
            <div
              className="h-full rounded-full bg-primary transition-[width]"
              style={{ width: `${Math.max(2, Math.round((d.value / max) * 100))}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// --- Categorical palette (shared by donut / legend dots) --------------------

/** Stroke classes for donut arcs. */
const SERIES_STROKE = [
  'stroke-indigo-500',
  'stroke-emerald-500',
  'stroke-sky-500',
  'stroke-amber-500',
  'stroke-rose-500',
  'stroke-violet-500',
  'stroke-teal-500',
  'stroke-orange-500',
];
/** Matching background dots for legends. */
const SERIES_BG = [
  'bg-indigo-500',
  'bg-emerald-500',
  'bg-sky-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-violet-500',
  'bg-teal-500',
  'bg-orange-500',
];

// --- Delta badge (comparative up/down chip) ---------------------------------

/**
 * A signed change chip with a direction arrow, coloured success (good) / danger
 * (bad) / muted (flat). `invert` flips the colour meaning for "lower is better"
 * metrics (e.g. drop-off, cost). The value is REAL — always derive it from data.
 */
export function DeltaBadge({
  value,
  format = (v) => `${v > 0 ? '+' : ''}${formatPct(v)}`,
  invert = false,
  neutralLabel,
  className = '',
}: {
  value: number;
  format?: (n: number) => string;
  invert?: boolean;
  neutralLabel?: string;
  className?: string;
}) {
  const positive = value > 0;
  const negative = value < 0;
  const good = invert ? negative : positive;
  const bad = invert ? positive : negative;
  const tone = good ? 'text-success' : bad ? 'text-danger' : 'text-muted';
  const arrow = positive ? '▲' : negative ? '▼' : '■';
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-semibold tabular-nums ${tone} ${className}`}
    >
      <span aria-hidden>{arrow}</span>
      {value === 0 && neutralLabel ? neutralLabel : format(value)}
    </span>
  );
}

// --- Rich KPI tile (label + value + delta + sparkline) ----------------------

/**
 * The modern KPI tile: an uppercase label, a big value, an optional comparative
 * `delta` chip and an optional real `spark` series. Every optional slot degrades
 * to nothing when the data is absent — never a fabricated trend.
 */
export function KpiTile({
  label,
  value,
  delta,
  spark,
  sparkClassName,
  accent = 'text-foreground',
  icon,
  hint,
}: {
  label: ReactNode;
  value: ReactNode;
  delta?: ReactNode;
  spark?: number[];
  sparkClassName?: string;
  accent?: string;
  icon?: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <div className="flex flex-col rounded-token-lg border border-border bg-surface px-4 py-3 shadow-elevation-1">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">{label}</span>
        {icon != null && (
          <span className="text-base leading-none text-muted" aria-hidden>
            {icon}
          </span>
        )}
      </div>
      <div className="mt-1 flex flex-wrap items-end justify-between gap-x-2 gap-y-1">
        <span className={`text-2xl font-bold tabular-nums ${accent}`}>{value}</span>
        {delta}
      </div>
      {spark != null && spark.length > 1 && (
        <div className="mt-2">
          <Sparkline
            values={spark}
            width={180}
            height={26}
            className={sparkClassName ?? accent}
          />
        </div>
      )}
      {hint != null && <div className="mt-1 text-xs text-muted">{hint}</div>}
    </div>
  );
}

// --- Radial gauge (single fraction) -----------------------------------------

/**
 * A circular progress gauge for a single 0..1 fraction (or value/max). The hue
 * comes from the caller's `text-*` class so it adapts to light / dark. The
 * centre shows a caller-supplied value label (falls back to the percentage).
 */
export function Gauge({
  value,
  max = 1,
  label,
  valueLabel,
  className = 'text-primary',
  size = 120,
}: {
  value: number;
  max?: number;
  label?: ReactNode;
  valueLabel?: ReactNode;
  className?: string;
  size?: number;
}) {
  const frac = max <= 0 ? 0 : Math.max(0, Math.min(1, value / max));
  const r = 42;
  const c = 2 * Math.PI * r;
  const dash = frac * c;
  return (
    <div className="flex flex-col items-center">
      <div className={`relative ${className}`} style={{ width: size, height: size }}>
        <svg
          viewBox="0 0 100 100"
          width={size}
          height={size}
          role="img"
          aria-label={typeof label === 'string' ? label : 'Jauge'}
        >
          <circle
            cx="50"
            cy="50"
            r={r}
            fill="none"
            stroke="currentColor"
            strokeOpacity={0.14}
            strokeWidth={9}
          />
          <circle
            cx="50"
            cy="50"
            r={r}
            fill="none"
            stroke="currentColor"
            strokeWidth={9}
            strokeLinecap="round"
            strokeDasharray={`${dash.toFixed(2)} ${(c - dash).toFixed(2)}`}
            transform="rotate(-90 50 50)"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <span className="text-xl font-bold tabular-nums text-foreground">
            {valueLabel ?? formatPct(frac * 100)}
          </span>
        </div>
      </div>
      {label != null && <div className="mt-1 text-center text-xs text-muted">{label}</div>}
    </div>
  );
}

// --- Donut chart (proportions + legend) -------------------------------------

export interface DonutSlice {
  label: string;
  value: number;
}

/**
 * A donut of proportional slices with an inline legend (label · value · %). All
 * slices are drawn from REAL values; an all-zero dataset renders just the empty
 * ring so the caller can pair it with an honest empty state.
 */
export function DonutChart({
  data,
  size = 168,
  valueFormat = compactNumber,
  centerValue,
  centerLabel,
}: {
  data: DonutSlice[];
  size?: number;
  valueFormat?: (n: number) => string;
  centerValue?: ReactNode;
  centerLabel?: ReactNode;
}) {
  const total = data.reduce((s, d) => s + Math.max(0, d.value), 0);
  const r = 42;
  const c = 2 * Math.PI * r;
  let acc = 0;
  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row">
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg viewBox="0 0 100 100" width={size} height={size} role="img" aria-label="Répartition">
          <circle cx="50" cy="50" r={r} fill="none" className="stroke-surface-2" strokeWidth={12} />
          {total > 0 &&
            data.map((d, i) => {
              const len = (Math.max(0, d.value) / total) * c;
              const el = (
                <circle
                  key={`${d.label}-${i}`}
                  cx="50"
                  cy="50"
                  r={r}
                  fill="none"
                  className={SERIES_STROKE[i % SERIES_STROKE.length]}
                  strokeWidth={12}
                  strokeDasharray={`${len.toFixed(2)} ${(c - len).toFixed(2)}`}
                  strokeDashoffset={(-acc).toFixed(2)}
                  transform="rotate(-90 50 50)"
                />
              );
              acc += len;
              return el;
            })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          {centerValue != null && (
            <span className="text-xl font-bold tabular-nums text-foreground">{centerValue}</span>
          )}
          {centerLabel != null && <span className="text-[11px] text-muted">{centerLabel}</span>}
        </div>
      </div>
      <ul className="min-w-0 flex-1 space-y-1.5 self-stretch">
        {data.map((d, i) => (
          <li key={`${d.label}-${i}`} className="flex items-center gap-2 text-xs">
            <span
              className={`h-2.5 w-2.5 shrink-0 rounded-full ${SERIES_BG[i % SERIES_BG.length]}`}
              aria-hidden
            />
            <span className="min-w-0 flex-1 truncate capitalize text-foreground" title={d.label}>
              {d.label}
            </span>
            <span className="shrink-0 tabular-nums text-muted">{valueFormat(d.value)}</span>
            <span className="w-9 shrink-0 text-right tabular-nums text-muted">
              {total > 0 ? `${Math.round((Math.max(0, d.value) / total) * 100)}%` : '—'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// --- Heatmap (generic intensity grid) ---------------------------------------

/**
 * A generic intensity grid: `data[row][col]` values shaded by `currentColor`
 * opacity (hue from the caller's `text-*` class). Empty cells stay transparent
 * over a faint track so the grid reads even with sparse REAL data. Scrolls on
 * narrow viewports.
 */
export function Heatmap({
  data,
  rowLabels,
  colLabels,
  colTick = 1,
  className = 'text-primary',
  cellTitle,
  cell = 'h-5 w-5',
}: {
  data: number[][];
  rowLabels: string[];
  colLabels: string[];
  colTick?: number;
  className?: string;
  cellTitle?: (row: number, col: number, value: number) => string;
  cell?: string;
}) {
  const max = Math.max(1, ...data.flat());
  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className="border-separate border-spacing-1">
        <thead>
          <tr>
            <th className="p-0" />
            {colLabels.map((cl, ci) => (
              <th key={ci} className="p-0 text-center text-[10px] font-medium text-muted">
                {ci % colTick === 0 ? cl : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowLabels.map((rl, ri) => (
            <tr key={ri}>
              <td className="whitespace-nowrap pr-1 text-right text-[10px] font-medium text-muted">
                {rl}
              </td>
              {colLabels.map((_, ci) => {
                const v = data[ri]?.[ci] ?? 0;
                return (
                  <td key={ci} className="p-0">
                    <div
                      className={`overflow-hidden rounded-sm bg-surface-2 ${cell}`}
                      title={cellTitle ? cellTitle(ri, ci, v) : `${rl} · ${colLabels[ci]} · ${v}`}
                    >
                      <div
                        className="h-full w-full bg-current"
                        style={{ opacity: v <= 0 ? 0 : Math.max(0.12, v / max) }}
                      />
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
